// src/pages/PreiseVerwalten.jsx
import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import Button from "../components/ui/Button";
import {
  PlusCircle,
  Trash2,
  Save,
  Sparkles,
  AlertTriangle,
  Info,
  CheckCircle2,
  XCircle,
  Minus,
  Plus,
  Trophy,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// === Domänen-Konstanten ===
const EVENT_TYPES = ["Erwachsene", "Kinder"];
const TEAM_TYPES = ["Alle", "Männer", "Frauen", "Mixed"];
const RANK_TYPES = ["Teamtyp", "Gesamt"];

const TEAM_TYPE_LABEL = {
  Alle: "Alle Team-Typen",
  Männer: "Nur Männer",
  Frauen: "Nur Frauen",
  Mixed: "Nur Mixed",
};

const TEAM_TYPE_CHIP = {
  Alle: "Alle",
  Männer: "Männer",
  Frauen: "Frauen",
  Mixed: "Mixed",
};

const RANK_TYPE_LABEL = {
  Teamtyp: "Platz im Teamtyp",
  Gesamt: "Gesamtrang im Event",
};

const RANK_TYPE_CHIP = {
  Teamtyp: "im Teamtyp",
  Gesamt: "Gesamt",
};

const RANK_TYPE_SENTENCE = {
  Teamtyp: "im Teamtyp",
  Gesamt: "in der Gesamtwertung",
};

// Eindeutige lokale IDs (nur im Browser, werden nicht gespeichert)
let idCounter = 0;
const makeId = () => `p_${Date.now()}_${idCounter++}`;

// Baut aus einer Regel einen Klartext-Satz: "Erwachsene · nur Männer · 1. Platz im Teamtyp"
function describeRule(p) {
  const parts = [p.eventType];
  parts.push(p.teamType === "Alle" ? "alle Typen" : `nur ${p.teamType}`);
  const rankNum = parseInt(p.rank, 10);
  if (rankNum > 0) {
    parts.push(`${rankNum}. Platz ${RANK_TYPE_SENTENCE[p.rankType]}`);
  } else {
    parts.push("Platz noch offen");
  }
  return parts.join(" · ");
}

// Prüft eine Regel im Kontext aller Regeln → { errors, warnings }
function validateRule(p, all) {
  const errors = [];
  const warnings = [];

  if (!p.title.trim()) errors.push("Name fehlt");

  const rankNum = parseInt(p.rank, 10);
  if (!rankNum || rankNum <= 0) errors.push("Platz muss mindestens 1 sein");

  if (p.rankType === "Gesamt" && p.teamType !== "Alle") {
    warnings.push(
      "Gesamtrang + Teamtyp-Filter ist ungewöhnlich: Beim Gesamtrang zählt der Platz im ganzen Event, unabhängig vom Teamtyp."
    );
  }

  if (rankNum > 0) {
    const dup = all.some(
      (o) =>
        o._id !== p._id &&
        o.eventType === p.eventType &&
        o.teamType === p.teamType &&
        o.rankType === p.rankType &&
        parseInt(o.rank, 10) === rankNum
    );
    if (dup)
      warnings.push(
        "Es gibt eine identische Regel: Bei der Preisverleihung greift nur die erste davon."
      );
  }

  return { errors, warnings };
}

// Vorschlag für einen Preisnamen bei Vorlagen
function suggestTitle(rank, teamType) {
  const suffix = teamType === "Alle" ? "" : ` ${teamType}`;
  return `${rank}. Platz${suffix}`;
}

// === Kleine, wiederverwendbare Bausteine ===

// Segment-/Chip-Umschalter statt Dropdown
function OptionToggle({ label, value, onChange, options }) {
  return (
    <div>
      <span className="block text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
        {label}
      </span>
      <div className="inline-flex flex-wrap gap-1 rounded-lg bg-secondary-950 border border-secondary-800 p-1">
        {options.map((o) => {
          const active = value === o.value;
          return (
            <button
              key={o.value}
              type="button"
              onClick={() => onChange(o.value)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                active
                  ? "bg-primary-500 text-secondary-950 shadow"
                  : "text-gray-300 hover:bg-secondary-800 hover:text-white"
              }`}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Medaillen-Plakette, farbig nach Platz
function Medal({ rank }) {
  const n = parseInt(rank, 10);
  const theme =
    n === 1
      ? "bg-gradient-to-br from-yellow-300 to-amber-500 text-amber-950 ring-amber-300/40"
      : n === 2
      ? "bg-gradient-to-br from-gray-200 to-gray-400 text-gray-800 ring-gray-300/40"
      : n === 3
      ? "bg-gradient-to-br from-amber-500 to-amber-700 text-amber-50 ring-amber-500/40"
      : n > 0
      ? "bg-gradient-to-br from-secondary-700 to-secondary-800 text-white ring-secondary-600/40"
      : "bg-secondary-800 text-gray-500 ring-secondary-700/40 border border-dashed border-secondary-600";

  return (
    <div
      className={`w-14 h-14 rounded-full flex items-center justify-center font-extrabold text-xl ring-4 ${theme}`}
    >
      {n > 0 ? (
        <span className="leading-none">
          {n}
          <span className="text-xs align-super">.</span>
        </span>
      ) : (
        <Trophy className="w-6 h-6" />
      )}
    </div>
  );
}

export default function PreiseVerwalten() {
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [banner, setBanner] = useState(null); // { type: "success" | "error", text }
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [tplOpen, setTplOpen] = useState(false);
  const [tpl, setTpl] = useState({
    eventType: "Erwachsene",
    teamType: "Alle",
    rankType: "Teamtyp",
    upTo: 3,
  });
  const navigate = useNavigate();
  const cardRefs = useRef({});

  // === 1️⃣ Preise laden ===
  useEffect(() => {
    async function load() {
      try {
        const data = await fetchWithAuth(`${BACKEND_URL}/awards`, navigate);
        const prepared = (data || []).map((p) => ({
          // Feste ID aus dem Backend uebernehmen: die Doppeltreffer-
          // Entscheidungen der Siegerehrung verweisen darauf. Eine neu
          // gewuerfelte ID bei jedem Laden haette sie beim Speichern entwertet.
          _id: p.id || makeId(),
          title: p.title ?? "",
          eventType: EVENT_TYPES.includes(p.eventType) ? p.eventType : "Erwachsene",
          rank: p.rank != null ? String(p.rank) : "",
          teamType: TEAM_TYPES.includes(p.teamType) ? p.teamType : "Alle",
          rankType: RANK_TYPES.includes(p.rankType) ? p.rankType : "Teamtyp",
          description: p.description ?? "",
          sponsor: p.sponsor ?? "",
        }));
        setPrices(prepared);
      } catch (err) {
        console.error("[PreiseVerwalten] Fehler beim Laden:", err);
        setBanner({ type: "error", text: "Fehler beim Laden der Preise." });
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [navigate]);

  // === Validierung aller Regeln (memoisiert) ===
  const validation = useMemo(() => {
    const map = {};
    let errorCount = 0;
    let warningCount = 0;
    for (const p of prices) {
      const v = validateRule(p, prices);
      map[p._id] = v;
      errorCount += v.errors.length;
      warningCount += v.warnings.length;
    }
    return { map, errorCount, warningCount };
  }, [prices]);

  // === Gruppierung nach Event (Reihenfolge innerhalb stabil) ===
  const groups = useMemo(
    () =>
      EVENT_TYPES.map((ev) => ({
        event: ev,
        items: prices.filter((p) => p.eventType === ev),
      })),
    [prices]
  );

  // === Mutationen ===
  const markDirty = () => {
    setDirty(true);
    setBanner(null);
  };

  const handleChange = (id, field, value) => {
    if (field === "rank") value = String(value).replace(/[^0-9]/g, "");
    setPrices((prev) =>
      prev.map((p) => (p._id === id ? { ...p, [field]: value } : p))
    );
    markDirty();
  };

  const stepRank = (id, delta) => {
    setPrices((prev) =>
      prev.map((p) => {
        if (p._id !== id) return p;
        const next = Math.max(1, (parseInt(p.rank, 10) || 0) + delta);
        return { ...p, rank: String(next) };
      })
    );
    markDirty();
  };

  const addPrice = (eventType = "Erwachsene") => {
    const id = makeId();
    setPrices((prev) => [
      ...prev,
      {
        _id: id,
        title: "",
        eventType,
        rank: "",
        teamType: "Alle",
        rankType: "Teamtyp",
        description: "",
        sponsor: "",
      },
    ]);
    markDirty();
    setTimeout(() => {
      const el = cardRefs.current[id];
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.querySelector("input[data-role='title']")?.focus();
      }
    }, 50);
  };

  const handleDelete = (id) => {
    setPrices((prev) => prev.filter((p) => p._id !== id));
    setConfirmDeleteId(null);
    markDirty();
  };

  const applyTemplate = () => {
    const upTo = Math.min(20, Math.max(1, parseInt(tpl.upTo, 10) || 1));
    const existing = new Set(
      prices
        .filter(
          (p) =>
            p.eventType === tpl.eventType &&
            p.teamType === tpl.teamType &&
            p.rankType === tpl.rankType
        )
        .map((p) => parseInt(p.rank, 10))
    );
    const added = [];
    for (let r = 1; r <= upTo; r++) {
      if (existing.has(r)) continue; // keine Doppelregeln erzeugen
      added.push({
        _id: makeId(),
        title: suggestTitle(r, tpl.teamType),
        eventType: tpl.eventType,
        rank: String(r),
        teamType: tpl.teamType,
        rankType: tpl.rankType,
        description: "",
        sponsor: "",
      });
    }
    if (added.length === 0) {
      setBanner({
        type: "error",
        text: "Diese Plätze existieren für die gewählte Kategorie bereits.",
      });
      return;
    }
    setPrices((prev) => [...prev, ...added]);
    setTplOpen(false);
    markDirty();
  };

  // === Speichern ===
  const handleSave = async () => {
    if (isSaving) return;

    if (validation.errorCount > 0) {
      setBanner({
        type: "error",
        text: `Bitte behebe zuerst ${validation.errorCount} Fehler (rot markiert).`,
      });
      return;
    }

    setIsSaving(true);
    setBanner(null);
    try {
      const pricesToSave = prices.map((p) => ({
        id: p._id,
        title: p.title.trim(),
        eventType: p.eventType,
        rank: parseInt(p.rank, 10),
        teamType: p.teamType,
        rankType: p.rankType,
        description: p.description.trim(),
        sponsor: (p.sponsor || "").trim(),
      }));

      const res = await fetchWithAuth(`${BACKEND_URL}/awards`, navigate, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(pricesToSave),
      });

      if (!res?.success) throw new Error("Serverfehler beim Speichern");

      setDirty(false);
      setBanner({
        type: "success",
        text: `${pricesToSave.length} Preise gespeichert.`,
      });
    } catch (err) {
      console.error("[PreiseVerwalten] Fehler beim Speichern:", err);
      setBanner({ type: "error", text: "Fehler beim Speichern der Preise." });
    } finally {
      setIsSaving(false);
    }
  };

  // === UI ===
  if (loading)
    return (
      <div className="p-6 bg-secondary-950 text-white min-h-screen">
        <p>Lade Preise...</p>
      </div>
    );

  const selectClass =
    "w-full bg-secondary-800 border border-secondary-700 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500";

  return (
    <div className="p-4 sm:p-6 bg-secondary-950 text-white min-h-screen pb-28 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold text-primary-500">Preise verwalten</h1>
        <Button onClick={() => navigate(-1)} variant="secondary">
          Zurück
        </Button>
      </div>

      {/* Erklärung */}
      <div className="flex items-start gap-2 text-sm text-gray-400 bg-secondary-900 border border-secondary-800 rounded-lg p-3 mb-5">
        <Info className="w-4 h-4 mt-0.5 shrink-0 text-primary-400" />
        <p>
          Jeder Preis ist eine Regel, die bei der Siegerehrung automatisch einem
          Team zugeordnet wird. Der graue Satz unter jedem Preis zeigt, welches
          Team ihn bekommt.
        </p>
      </div>

      {/* Statusbanner */}
      {banner && (
        <div
          className={`flex items-center gap-2 rounded-lg p-3 mb-5 border ${
            banner.type === "success"
              ? "bg-green-900/30 border-green-700 text-green-300"
              : "bg-red-900/30 border-red-700 text-red-300"
          }`}
        >
          {banner.type === "success" ? (
            <CheckCircle2 className="w-5 h-5 shrink-0" />
          ) : (
            <XCircle className="w-5 h-5 shrink-0" />
          )}
          <span>{banner.text}</span>
        </div>
      )}

      {/* Schnellstart-Vorlage */}
      <div className="bg-secondary-900 border border-secondary-800 rounded-lg mb-6">
        <button
          onClick={() => setTplOpen((o) => !o)}
          className="w-full flex items-center gap-2 p-4 text-left font-semibold"
        >
          <Sparkles className="w-5 h-5 text-primary-400" />
          Schnellstart: Podium-Preise erzeugen
          <span className="ml-auto text-gray-400 text-sm">
            {tplOpen ? "▲" : "▼"}
          </span>
        </button>
        {tplOpen && (
          <div className="border-t border-secondary-800 p-4 space-y-4">
            <div className="flex flex-wrap gap-x-6 gap-y-4">
              <OptionToggle
                label="Event"
                value={tpl.eventType}
                onChange={(v) => setTpl((t) => ({ ...t, eventType: v }))}
                options={EVENT_TYPES.map((ev) => ({ value: ev, label: ev }))}
              />
              <OptionToggle
                label="Teamtyp"
                value={tpl.teamType}
                onChange={(v) => setTpl((t) => ({ ...t, teamType: v }))}
                options={TEAM_TYPES.map((tt) => ({
                  value: tt,
                  label: TEAM_TYPE_CHIP[tt],
                }))}
              />
              <OptionToggle
                label="Wertung"
                value={tpl.rankType}
                onChange={(v) => setTpl((t) => ({ ...t, rankType: v }))}
                options={RANK_TYPES.map((rt) => ({
                  value: rt,
                  label: RANK_TYPE_CHIP[rt],
                }))}
              />
              <label className="flex flex-col gap-1">
                <span className="block text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
                  Plätze bis
                </span>
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={tpl.upTo}
                  onChange={(e) =>
                    setTpl((t) => ({ ...t, upTo: e.target.value }))
                  }
                  className={`${selectClass} w-20`}
                />
              </label>
            </div>
            <p className="text-sm text-gray-400">
              Erzeugt Preise für Platz 1 bis {tpl.upTo || 1} –{" "}
              <span className="text-gray-300">
                {tpl.eventType} · {TEAM_TYPE_LABEL[tpl.teamType]} ·{" "}
                {RANK_TYPE_LABEL[tpl.rankType]}
              </span>
              . Bereits vorhandene Plätze werden übersprungen.
            </p>
            <Button onClick={applyTemplate} className="flex items-center gap-2">
              <Sparkles size={16} /> Preise erzeugen
            </Button>
          </div>
        )}
      </div>

      {/* Preis-Gruppen nach Event */}
      {groups.map((group) => (
        <section key={group.event} className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-white">
              {group.event}
              <span className="ml-2 text-sm font-normal text-gray-500">
                {group.items.length}{" "}
                {group.items.length === 1 ? "Preis" : "Preise"}
              </span>
            </h2>
            <button
              onClick={() => addPrice(group.event)}
              className="flex items-center gap-1 text-sm text-primary-400 hover:text-primary-300"
            >
              <PlusCircle size={16} /> Preis für {group.event}
            </button>
          </div>

          {group.items.length === 0 ? (
            <p className="text-gray-500 text-sm italic border border-dashed border-secondary-800 rounded-lg p-4">
              Noch keine Preise für {group.event}.
            </p>
          ) : (
            <div className="space-y-4">
              {group.items.map((p) => {
                const v = validation.map[p._id] || { errors: [], warnings: [] };
                const hasError = v.errors.length > 0;
                return (
                  <div
                    key={p._id}
                    ref={(el) => (cardRefs.current[p._id] = el)}
                    className={`bg-secondary-900 border rounded-xl overflow-hidden ${
                      hasError ? "border-red-700" : "border-secondary-800"
                    }`}
                  >
                    {/* Kopfbereich: Medaille + Name + Platz-Stepper */}
                    <div className="flex items-start gap-4 p-4">
                      {/* Medaille + Platz-Stepper */}
                      <div className="flex flex-col items-center gap-2 shrink-0">
                        <Medal rank={p.rank} />
                        <div className="flex items-center rounded-lg border border-secondary-700 bg-secondary-950 overflow-hidden">
                          <button
                            type="button"
                            onClick={() => stepRank(p._id, -1)}
                            className="p-1.5 text-gray-400 hover:text-white hover:bg-secondary-800"
                            aria-label="Platz verringern"
                          >
                            <Minus size={14} />
                          </button>
                          <input
                            type="text"
                            inputMode="numeric"
                            value={p.rank}
                            placeholder="–"
                            onChange={(e) =>
                              handleChange(p._id, "rank", e.target.value)
                            }
                            className="w-9 text-center bg-transparent text-white text-sm py-1 focus:outline-none"
                            aria-label="Platz"
                          />
                          <button
                            type="button"
                            onClick={() => stepRank(p._id, 1)}
                            className="p-1.5 text-gray-400 hover:text-white hover:bg-secondary-800"
                            aria-label="Platz erhöhen"
                          >
                            <Plus size={14} />
                          </button>
                        </div>
                      </div>

                      {/* Name + Löschen */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-2">
                          <input
                            type="text"
                            data-role="title"
                            value={p.title}
                            placeholder="Preisname …"
                            onChange={(e) =>
                              handleChange(p._id, "title", e.target.value)
                            }
                            className="flex-1 min-w-0 bg-transparent text-lg font-bold text-white placeholder-gray-600 border-b border-transparent hover:border-secondary-700 focus:border-primary-500 focus:outline-none pb-1 transition-colors"
                          />
                          <button
                            onClick={() => setConfirmDeleteId(p._id)}
                            className="p-1.5 text-gray-500 hover:text-red-400 rounded-md hover:bg-secondary-800 shrink-0"
                            aria-label="Preis löschen"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>

                        {/* Klartext-Vorschau */}
                        <p className="mt-1.5 text-sm text-gray-400">
                          <span className="text-gray-500">Vergeben an: </span>
                          <span className="font-medium text-gray-300">
                            {describeRule(p)}
                          </span>
                        </p>
                      </div>
                    </div>

                    {/* Regel-Einstellungen */}
                    <div className="px-4 pb-4 space-y-4">
                      <div className="flex flex-wrap gap-x-6 gap-y-4 pt-1">
                        <OptionToggle
                          label="Event"
                          value={p.eventType}
                          onChange={(val) =>
                            handleChange(p._id, "eventType", val)
                          }
                          options={EVENT_TYPES.map((ev) => ({
                            value: ev,
                            label: ev,
                          }))}
                        />
                        <OptionToggle
                          label="Teamtyp"
                          value={p.teamType}
                          onChange={(val) =>
                            handleChange(p._id, "teamType", val)
                          }
                          options={TEAM_TYPES.map((tt) => ({
                            value: tt,
                            label: TEAM_TYPE_CHIP[tt],
                          }))}
                        />
                        <OptionToggle
                          label="Wertung"
                          value={p.rankType}
                          onChange={(val) =>
                            handleChange(p._id, "rankType", val)
                          }
                          options={RANK_TYPES.map((rt) => ({
                            value: rt,
                            label: RANK_TYPE_CHIP[rt],
                          }))}
                        />
                      </div>

                      {/* Sponsor */}
                      <input
                        type="text"
                        value={p.sponsor || ""}
                        maxLength={120}
                        placeholder="Sponsor des Preises (optional)"
                        onChange={(e) =>
                          handleChange(p._id, "sponsor", e.target.value)
                        }
                        className="w-full bg-secondary-800 border border-secondary-700 text-white p-2.5 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />

                      {/* Beschreibung */}
                      <textarea
                        value={p.description}
                        placeholder="Beschreibung oder Inhalt des Preises (optional)"
                        onChange={(e) =>
                          handleChange(p._id, "description", e.target.value)
                        }
                        className="w-full bg-secondary-800 border border-secondary-700 text-white p-2.5 rounded-lg min-h-[56px] text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />

                      {/* Fehler */}
                      {v.errors.map((msg, k) => (
                        <div
                          key={`e${k}`}
                          className="flex items-center gap-2 text-sm text-red-400"
                        >
                          <XCircle className="w-4 h-4 shrink-0" /> {msg}
                        </div>
                      ))}

                      {/* Warnungen */}
                      {v.warnings.map((msg, k) => (
                        <div
                          key={`w${k}`}
                          className="flex items-start gap-2 text-sm text-yellow-400"
                        >
                          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                          <span>{msg}</span>
                        </div>
                      ))}
                    </div>

                    {/* Lösch-Bestätigung */}
                    {confirmDeleteId === p._id && (
                      <div className="flex items-center justify-end gap-3 text-sm bg-secondary-950 border-t border-secondary-800 px-4 py-2.5">
                        <span className="text-gray-400 mr-auto">
                          Diesen Preis wirklich löschen?
                        </span>
                        <button
                          onClick={() => handleDelete(p._id)}
                          className="text-red-400 hover:text-red-300 font-semibold"
                        >
                          Ja, löschen
                        </button>
                        <button
                          onClick={() => setConfirmDeleteId(null)}
                          className="text-gray-400 hover:text-gray-200"
                        >
                          Abbrechen
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      ))}

      {/* Neuer Preis (allgemein) */}
      <Button
        onClick={() => addPrice("Erwachsene")}
        variant="secondary"
        className="flex items-center gap-2"
      >
        <PlusCircle size={16} /> Neuer Preis
      </Button>

      {/* Sticky Aktionsleiste */}
      <div className="fixed bottom-0 left-0 right-0 bg-secondary-900/95 backdrop-blur border-t border-secondary-800 p-3 flex items-center gap-3">
        <div className="text-sm text-gray-400 flex items-center gap-3">
          {validation.errorCount > 0 && (
            <span className="text-red-400 flex items-center gap-1">
              <XCircle className="w-4 h-4" /> {validation.errorCount} Fehler
            </span>
          )}
          {validation.warningCount > 0 && (
            <span className="text-yellow-400 flex items-center gap-1">
              <AlertTriangle className="w-4 h-4" /> {validation.warningCount}{" "}
              Warnungen
            </span>
          )}
          {dirty && validation.errorCount === 0 && (
            <span className="text-gray-400">Ungespeicherte Änderungen</span>
          )}
        </div>
        <Button
          onClick={handleSave}
          disabled={isSaving || validation.errorCount > 0}
          className="flex items-center gap-2 ml-auto"
        >
          {isSaving ? (
            "💾 Speichere..."
          ) : (
            <>
              <Save size={16} /> Speichern
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
